import {useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import TranslateIcon from "@mui/icons-material/Translate";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {Controller, useFormContext} from "react-hook-form";
import {toast} from "sonner";
import {Box, Colors, Grid, Text} from "@bounce/shared/ui";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {useFetchTemplateMessage} from "../../../apis/requests/templates/fetchTemplateMessage";
import {Button, TextField} from "../../../ui/components";
import CollapsibleHtmlField from "../CollapsibleHtmlField";

const RETRANSLATE_WARNING =
  "Re-translating will overwrite the current Spanish content, including any manual changes you've made. This can't be undone.";

export type SpanishFields = {
  subject: string;
  pre_header: string;
  body: string;
  landing_page_body: string;
  channel_message_code: string;
};

export type TranslationStatus = "empty" | "translating" | "translated" | "error";

type Props = {
  onTranslate: () => void;
  onSave: () => void;
  isTranslating: boolean;
  isSaving: boolean;
  showPreHeader: boolean;
  showBody: boolean;
  showLandingPageBody: boolean;
  showChannelMessageCode: boolean;
  translationStatus: TranslationStatus;
  translationError?: string;
  isNewTemplate?: boolean;
};

const EMPTY_TITLE = "Not translated yet";
const EMPTY_DESCRIPTION = "This template doesn't have a Spanish translation. Click below to auto-translate from the English version.";
const UNSAVED_DESCRIPTION = "Save your template first, then you'll be able to auto-translate into Spanish.";
const TRANSLATING_TITLE = "Translation in progress";
const TRANSLATING_DESCRIPTION = "We're translating your English content into Spanish. This usually takes a few minutes.";
const ERROR_TITLE = "Translation failed";
const DEFAULT_ERROR = "Something went wrong while translating. Please try again.";

const stateContainerSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  py: 6,
  width: "100%",
  backgroundColor: Colors.tertiary._100,
  borderRadius: "12px",
} as const;

const SpanishEmptyState = ({onTranslate, disabled, isNewTemplate}: {onTranslate: () => void; disabled: boolean; isNewTemplate: boolean}) => (
  <Box sx={stateContainerSx}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        backgroundColor: Colors.tertiary._200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <TranslateIcon sx={{fontSize: 24, color: Colors.grey._500}} />
    </Box>
    <Text variant="inter600" size={14} sx={{color: Colors.grey._700}}>
      {EMPTY_TITLE}
    </Text>
    <Text size={13} sx={{color: Colors.grey._500, textAlign: "center", maxWidth: 360}}>
      {isNewTemplate ? UNSAVED_DESCRIPTION : EMPTY_DESCRIPTION}
    </Text>
    <Button buttonType="contained-primary" size="small" onClick={onTranslate} disabled={disabled || isNewTemplate}>
      Translate from English
    </Button>
  </Box>
);

const SpanishTranslatingState = () => (
  <Box sx={stateContainerSx}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        backgroundColor: Colors.blue._100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <TranslateIcon sx={{fontSize: 24, color: Colors.blue._600}} />
    </Box>
    <Text variant="inter600" size={14} sx={{color: Colors.grey._700}}>
      {TRANSLATING_TITLE}
    </Text>
    <Text size={13} sx={{color: Colors.grey._500, textAlign: "center", maxWidth: 360}}>
      {TRANSLATING_DESCRIPTION}
    </Text>
    <Button buttonType="contained-primary" size="small" disabled>
      Translating…
    </Button>
  </Box>
);

const SpanishErrorState = ({onRetry, errorMessage}: {onRetry: () => void; errorMessage: string}) => (
  <Box sx={stateContainerSx}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        backgroundColor: Colors.red._100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ErrorOutlineIcon sx={{fontSize: 24, color: Colors.red._800}} />
    </Box>
    <Text variant="inter600" size={14} sx={{color: Colors.grey._700}}>
      {ERROR_TITLE}
    </Text>
    <Text size={13} sx={{color: Colors.red._800, textAlign: "center", maxWidth: 360}}>
      {errorMessage}
    </Text>
    <Button buttonType="contained-primary" size="small" onClick={onRetry}>
      Retry translation
    </Button>
  </Box>
);

const SpanishTranslatedState = ({
  onTranslate,
  onSave,
  isTranslating,
  isSaving,
  showPreHeader,
  showBody,
  showLandingPageBody,
  showChannelMessageCode,
}: Omit<Props, "translationStatus" | "translationError">) => {
  const {
    control,
    watch,
    setValue,
    formState: {isDirty},
  } = useFormContext<SpanishFields>();
  const [showBodyHtml, setShowBodyHtml] = useState(false);
  const [showLandingPageHtml, setShowLandingPageHtml] = useState(false);
  const channelMessageCode = watch("channel_message_code");
  const {mutate: fetchTemplateMessage, isPending: isFetchingTemplateMessage} = useFetchTemplateMessage();

  const handleRetranslate = () => {
    NiceModal.show(AreYouSureDialog, {
      onConfirm: onTranslate,
      contentTextId: RETRANSLATE_WARNING,
      confirmTextId: "Re-translate",
    });
  };

  const handleFetchTemplateMessage = () => {
    if (!channelMessageCode) return;
    fetchTemplateMessage(
      {code: channelMessageCode},
      {
        onSuccess: ({template_message}) => {
          if (!template_message) {
            toast.error("No message found for this code");
            return;
          }
          setValue("subject", template_message, {shouldDirty: true});
        },
      }
    );
  };

  return (
    <Box sx={{display: "flex", flexDirection: "column", gap: "24px", width: "100%"}}>
      <Grid container spacing={2.5} sx={{width: "100%"}}>
        {showChannelMessageCode && (
          <Grid size={12}>
            <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
              Channel message code (ES)
            </Text>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <Controller
                name="channel_message_code"
                control={control}
                render={({field: {ref, ...field}}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    size="small"
                    placeholder="Spanish message code"
                  />
                )}
              />
              <Button
                buttonType="outlined-primary"
                size="small"
                onClick={handleFetchTemplateMessage}
                disabled={!channelMessageCode || isFetchingTemplateMessage}
              >
                Fetch
              </Button>
            </Box>
          </Grid>
        )}
        <Grid size={12}>
          <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
            {showChannelMessageCode ? "Message (ES)" : "Subject (ES)"}
          </Text>
          <Controller
            name="subject"
            control={control}
            render={({field: {ref, ...field}}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                multiline
                minRows={showChannelMessageCode ? 4 : 1}
                placeholder={showChannelMessageCode ? "Hola {{first_name}}, ..." : "Email subject line (Spanish)"}
                disabled={showChannelMessageCode}
              />
            )}
          />
        </Grid>
        {showPreHeader && (
          <Grid size={12}>
            <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
              Pre-header (ES)
            </Text>
            <Controller
              name="pre_header"
              control={control}
              render={({field: {ref, ...field}}) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  size="small"
                  placeholder="Email pre-header line (Spanish)"
                />
              )}
            />
          </Grid>
        )}
        {showBody && (
          <Grid size={12}>
            <CollapsibleHtmlField
              name="body"
              label="Body / HTML (ES)"
              show={showBodyHtml}
              onToggle={setShowBodyHtml}
              minRows={6}
              placeholder="<html>...</html>"
            />
          </Grid>
        )}
        {showLandingPageBody && (
          <Grid size={12}>
            <CollapsibleHtmlField
              name="landing_page_body"
              label="Landing page HTML (ES)"
              show={showLandingPageHtml}
              onToggle={setShowLandingPageHtml}
              minRows={4}
              placeholder='<img src="..." />'
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{display: "flex", gap: 1, justifyContent: "flex-end"}}>
        <Button
          buttonType="outlined-primary"
          size="small"
          onClick={handleRetranslate}
          disabled={isSaving || isTranslating}
        >
          Re-translate
        </Button>
        <Button
          buttonType="contained-primary"
          size="small"
          onClick={onSave}
          disabled={isSaving || isTranslating || !isDirty}
        >
          {isSaving ? "Saving…" : "Save changes"}
        </Button>
      </Box>
    </Box>
  );
};

const SpanishTranslationCard = ({
  onTranslate,
  onSave,
  isTranslating,
  isSaving,
  showPreHeader,
  showBody,
  showLandingPageBody,
  showChannelMessageCode,
  translationStatus,
  translationError,
  isNewTemplate = false,
}: Props) => {
  switch (translationStatus) {
    case "empty":
      return <SpanishEmptyState onTranslate={onTranslate} disabled={isSaving} isNewTemplate={isNewTemplate} />;
    case "translating":
      return <SpanishTranslatingState />;
    case "error":
      return <SpanishErrorState onRetry={onTranslate} errorMessage={translationError ?? DEFAULT_ERROR} />;
    case "translated":
      return (
        <SpanishTranslatedState
          onTranslate={onTranslate}
          onSave={onSave}
          isTranslating={isTranslating}
          isSaving={isSaving}
          showPreHeader={showPreHeader}
          showBody={showBody}
          showLandingPageBody={showLandingPageBody}
          showChannelMessageCode={showChannelMessageCode}
        />
      );
  }
};

export default SpanishTranslationCard;
